import React, { lazy, useEffect, useState } from 'react';
import {
  QueryProvider, client, bool, string, shape, extend, arrayOf, number, alias, params,
  useDataModel
} from '@thd-nucleus/data-sources';
import { string as stringType } from 'prop-types';
import { useStoreId } from '@thd-nucleus/experience-context';
import { FulfillmentAlert } from './FulfillmentAlert';

const WrappedFulfillment = lazy(() => import(
  /* webpackChunkName: "fulfillment" */
  '@thd-olt-component-react/fulfillment'
).then((mod) => {
  return {
    default: (props) => {
      const { podFulFillmentUtils } = mod;

      return (
        <QueryProvider
          dataSource="catalog"
          cacheKey="selected-item-details-fulfillment"
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <FulfillmentAlert {...props} podFulFillmentUtils={podFulFillmentUtils} />
        </QueryProvider>
      );
    }
  };
}));

const Hovered = ({ hoveredItemId }) => {
  useDataModel('clientOnlyProduct', {
    variables: {
      itemId: hoveredItemId,
      storeId: useStoreId()
    },
    ssr: false
  });
  return null;
};

const FulfillmentLazy = ({ itemId, hoveredItemId }) => {
  const [previouslyHoveredIds, setPreviouslyHoveredIds] = useState([]);

  useEffect(() => {
    if (!previouslyHoveredIds.includes(hoveredItemId)) {
      setPreviouslyHoveredIds([...previouslyHoveredIds, hoveredItemId]);
    }
  }, [hoveredItemId]);

  const storeId = useStoreId();
  const { data, loading, error } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    ssr: false
  });

  if (loading || error) return <div />;

  return (
    <>
      <WrappedFulfillment product={data?.product} itemId={itemId} />
      {previouslyHoveredIds.map((hoverId) => <Hovered key={hoverId} hoveredItemId={hoverId} />)}
    </>
  );
};

FulfillmentLazy.dataModel = extend({
  clientOnlyProduct: alias('product').params({ itemId: string().isRequired(), dataSource: string() }).shape({
    itemId: string(),
    dataSources: string(),
    fulfillment: client(params({ storeId: string() }).shape({
      backordered: bool(),
      bossExcludedShipStates: string(),
      bodfsAssemblyEligible: bool(),
      inStoreAssemblyEligible: bool(),
      seasonStatusEligible: bool(),
      onlineStoreStatus: bool(),
      anchorStoreStatus: bool(),
      sthExcludedShipState: string(),
      bossExcludedShipState: string(),
      fulfillmentOptions: shape({
        type: string(),
        fulfillable: bool(),
        services: arrayOf(shape({
          type: string(),
          isBossDominant: bool(),
          mode: shape({
            code: string(),
            desc: string(),
            longDesc: string(),
            group: string()
          }),
          isDefault: bool(),
          deliveryCharge: string(),
          freeDeliveryThreshold: number(),
          hasFreeShipping: bool(),
          itemLevelFreeShipping: bool(),
          deliveryTimeline: string(),
          deliveryDates: shape({
            startDate: string(),
            endDate: string(),
            timeOfArrival: string()
          }),
          totalCharge: number(),
          pickupTime: string(),
          locations: arrayOf(shape({
            type: string(),
            locationId: string(),
            isAnchor: bool(),
            storeName: string(),
            storePhone: string(),
            state: string(),
            distance: number(),
            inventory: shape({
              quantity: number(),
              isInStock: bool(),
              isOutOfStock: bool(),
              isLimitedQuantity: bool(),
              isUnavailable: bool(),
              maxAllowedBopisQty: number(),
              minAllowedBopisQty: number(),
            }),
            curbsidePickupFlag: bool(),
            isBuyInStoreCheckNearBy: bool(),
          })),
        }))
      }),
    })),
    identifiers: shape({
      itemId: string(),
    })
  })
});

FulfillmentLazy.displayName = 'FulfillmentLazy';

FulfillmentLazy.propTypes = {
  itemId: stringType,
  hoveredItemId: stringType
};

FulfillmentLazy.defaultProps = {
  itemId: '',
  hoveredItemId: ''
};

export { FulfillmentLazy };
